// Matches standard chord names: root (A-G) + optional modifier + optional bass note
// Examples: G, Am, F#m7, A#m7(11), Bm/F#, Cadd9, Gsus4, D7, Ebmaj7, F7M/C, G6, B°, CΔ7
// Uses lookbehind (not preceded by letter) and lookahead (not followed by lowercase)
// to avoid matching roots inside regular words like "Example" or "BRIDGE".
const CHORD_RE = /(?<![A-Za-z])([A-G][b#]?(?:m(?:aj)?|dim|aug|[°º]|\u0394|sus[24]?|add\d+)?(?:\d+[M+]?)?(?:\([^)]*\))?(?:\/[A-G][b#]?)?)(?![a-z])/g;

// ── Typed token API (TX31) ────────────────────────────────────────────────────

export type ChordToken = { kind: 'chord'; text: string };
export type AnnotationToken = { kind: 'annotation'; text: string };
export type LyricToken = { kind: 'lyric'; text: string };
export type ContentToken = ChordToken | AnnotationToken | LyricToken;

const ANNOTATION_RE = /\{([^}]*)\}/g;

/**
 * Tokenise a single line of section content into typed tokens.
 * Annotation tags `{text}` are returned as-is (never transposed).
 * Chord tokens are detected by CHORD_RE; everything else is lyric.
 * Unmatched `{` without a closing `}` is treated as plain lyric text.
 */
export function tokeniseLine(line: string): ContentToken[] {
  const tokens: ContentToken[] = [];
  let cursor = 0;

  // Collect all annotation and chord positions in one pass
  type Span = { start: number; end: number; token: ContentToken };
  const spans: Span[] = [];

  // Annotation spans
  ANNOTATION_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = ANNOTATION_RE.exec(line)) !== null) {
    spans.push({ start: m.index, end: m.index + m[0].length, token: { kind: 'annotation', text: m[1] } });
  }

  // Chord spans (only outside annotation regions)
  CHORD_RE.lastIndex = 0;
  while ((m = CHORD_RE.exec(line)) !== null) {
    const inAnnotation = spans.some((s) => s.token.kind === 'annotation' && m!.index >= s.start && m!.index < s.end);
    if (!inAnnotation) {
      spans.push({ start: m.index, end: m.index + m[0].length, token: { kind: 'chord', text: m[0] } });
    }
  }

  spans.sort((a, b) => a.start - b.start);

  for (const span of spans) {
    if (span.start > cursor) {
      tokens.push({ kind: 'lyric', text: line.slice(cursor, span.start) });
    }
    tokens.push(span.token);
    cursor = span.end;
  }
  if (cursor < line.length) {
    tokens.push({ kind: 'lyric', text: line.slice(cursor) });
  }

  return tokens;
}

export function isChordLine(line: string): boolean {
  const normalizedLine = line.replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
  if (!normalizedLine) return false;

  const words = normalizedLine.split(/\s+/);
  const chordWords = words.filter((word) => {
    const tokens = tokeniseLine(word);
    return tokens.length === 1 && tokens[0].kind === 'chord' && tokens[0].text === word;
  });

  return chordWords.length / words.length >= 0.6;
}

export function parseChords(content: string): string[] {
  const matches = content.match(CHORD_RE);
  if (!matches) return [];
  // Deduplicate while preserving order
  return [...new Set(matches)];
}

export function parseSectionChords(content: string): string[] {
  return parseChords(content);
}
